import { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useFrontendAuth } from '../hooks/useAuth';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { disableMyPetitoNudges, getMyChildren, snoozeMyPetitoNudge } from '../services/api.shop';
import { ChildDto } from '../types';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { useLoginPrompt } from '../loginPrompts/loginPromptContext';

/**
 * After the home tour, ask accounts with zero children to use My Petito.
 * No phase-slider substitute — checklists and demand-side outgrown need a child.
 */
const PendingMyPetitoNudge = () => {
    const { isAuthenticated, isAuthReady, user } = useFrontendAuth();
    const { grantedId, reportWaiting, claim, settle } = useLoginPrompt();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queryClient = useQueryClient();
    const nudgesOff = !!user?.myPetitoNudgesDisabled;
    const snoozed =
        !!user?.myPetitoSnoozedUntil && new Date(user.myPetitoSnoozedUntil).getTime() > Date.now();

    const { data: children = [], isFetched } = useProtectedQuery<ChildDto[]>({
        queryKey: ['myChildren'],
        queryFn: async () => (await getMyChildren()).data,
        enabled: isAuthReady && isAuthenticated && !nudgesOff && !snoozed,
        staleTime: 60 * 1000,
    });

    const [finished, setFinished] = useState(false);
    const eligible = useMemo(
        () =>
            isAuthReady &&
            isAuthenticated &&
            !nudgesOff &&
            !snoozed &&
            isFetched &&
            children.length === 0 &&
            !finished,
        [isAuthReady, isAuthenticated, nudgesOff, snoozed, isFetched, children.length, finished]
    );

    const [open, setOpen] = useState(false);

    useEffect(() => {
        reportWaiting('mypetito-nudge', eligible);
    }, [eligible, reportWaiting]);

    useEffect(() => {
        if (grantedId !== 'mypetito-nudge' || !eligible) return;
        claim('mypetito-nudge');
        setOpen(true);
    }, [grantedId, eligible, claim]);

    if (!open) return null;

    const snooze = async () => {
        await snoozeMyPetitoNudge();
        await queryClient.invalidateQueries({ queryKey: ['user'] });
        setFinished(true);
        setOpen(false);
        settle();
    };

    const disable = async () => {
        await disableMyPetitoNudges();
        await queryClient.invalidateQueries({ queryKey: ['user'] });
        setFinished(true);
        setOpen(false);
        settle();
    };

    return (
        <Transition appear show as={Fragment}>
            <Dialog as="div" className="relative z-[80]" onClose={() => void snooze()}>
                <div className="fixed inset-0 bg-black/40" />
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-md rounded-2xl bg-[var(--bg-color)] p-5 shadow-xl">
                            <Dialog.Title className="text-lg font-semibold text-[var(--text-color)]">
                                {tr('mypetito.nudge.title', 'Want sizes and checklists that grow with you?')}
                            </Dialog.Title>
                            <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                {tr(
                                    'mypetito.nudge.body',
                                    'My Petito keeps phases, checklists, and outgrown tips for your child. The shop works without it — this is how we personalise.'
                                )}
                            </p>
                            <div className="mt-5 flex flex-col gap-2">
                                <Link
                                    to="/my-petito"
                                    onClick={() => {
                                        setFinished(true);
                                        setOpen(false);
                                        settle();
                                    }}
                                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--accent-color)] px-5 text-sm font-semibold text-white"
                                >
                                    {tr('mypetito.nudge.cta', 'Set up My Petito')}
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => void snooze()}
                                    className="min-h-11 text-sm font-medium underline"
                                >
                                    {tr('mypetito.nudge.later', 'Not now')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void disable()}
                                    className="min-h-11 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]"
                                >
                                    {tr('mypetito.nudge.never', 'Do not ask again')}
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default PendingMyPetitoNudge;
